import type { FileApi } from '@mimir/common/upload/fileApi';
import { UploadState } from '@mimir/common/upload/upload';
import { getRouterLink } from '../util/basicRoute';
import { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';
import type { LoadedItem, ItemUpdateRequestDto } from '@mimir/common/dto/searchResultHit';
import type { ExtendedItemDto } from '@mimir/common/dto/extendedItemDto';
import type {
  ItemCreationResponse,
  StandardItemEntryDto
} from '@mimir/common/dto/itemModels';
import type { ItemStateType } from '@mimir/common/dto/itemState';
import { ItemState } from '@mimir/common/dto/itemState';
import type { DynamoItemType } from '@mimir/common/dto/itemTypes';
import { ItemType } from '@mimir/common/dto/itemTypes';
import type { ItemMetaData, ItemMetaDataDto } from '@mimir/common/dto/fieldValue';
import type { MetadataErrors, MetadataSnapshot } from '@mimir/common/dto/mdfModels';
import { ItemMetadataUpdater, type UpdateMetadataParams } from '@mimir/client-common/metadata/itemMetadataUpdater';
import { createMetadataConflictError, createMetadataFormTypeConflictError } from '@mimir/client-common/metadata/metadataConflictTypes';
import { ConsoleLogger } from '@mimir/common/util/logging';
import { RelationType } from '@mimir/common/dto/relationsEntity';
import type { RouterLink } from '@mimir/common/dto/searchModels';
import type { MissingDependenciesDownloadInfo } from '@mimir/common/dto/sequenceDetails';
import type { SequenceDetails } from '@mimir/primitive/premiere/sequenceDetails';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import type { ErrorMessageFieldName, StateFieldName } from '@mimir/common/search/interfaces';
import { itemTypeFromMediaType, mediaTypeFromFilename } from '@mimir/common/util/mediaContentType';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import type { MultiPartUploader } from '@mimir/common/upload/multipartUploader';
import type { SimpleUploader } from '@mimir/common/upload/simpleUploader';
import { defaultAnalyticConfig, type AnalyticConfig, type UploadConfig } from '@mimir/common/dto/tenantModel';
import { constructUploadFolderPath, constructS3CloneUploadPath } from '../util/uploadPaths';
import type { PathItem } from '@mimir/primitive/pathItem';
import type { CreateItemUploadLockResponseDto } from '@mimir/common/dto/uploadLocationDto';
import { invariant } from '@mimir/common/util/invariant';
import type { TaskPriority } from '@mimir/common/async/taskPriority';
import { api_deleteItemUploadLock } from '@mimir/common/api/uploadApi';
import { api_getItemAncestry } from '../api/folderApi';
import { api_createItem, api_createPersonImage, api_updateItemRelations, api_updateItem, api_ingestFile, api_getItemFields } from '../util/api';
import { constructExportedSequenceFileName } from '../util/fileNames';
import { DEFAULT_UPLOAD_LOCATION } from '@mimir/common/dto/keldaConfigPrimitives';
import { getSourceForExternalCloudIngestedItem } from '@mimir/common/util/ingest';
import type { Canceler } from 'axios';
import type { FileSetProgress } from '@mimir/common/upload/uploadProgression';
import type { ProgressInfo} from '@mimir/common/dto/progress';
import { ProgressStep } from '@mimir/common/dto/progress';
import type { UploadMethods } from '@mimir/common/upload/upload-types';
import type { ItemAccessControl } from '@mimir/common/dto/itemAccessControlDto';
import { ExponentialBackoff, isRetryable } from '@mimir/common/async/resilience';
import { neverCancel } from '@mimir/common/async/async';
import type { IngestFileDto } from '@mimir/common/dto/ingestFileDtos';
import { allExceptionDetails } from '@mimir/common/util/exception';
import type { BackendClient } from '@mimir/common/util/backendClient';

// Used to have a unique key on new upload items
let lid = 0;

/**
 * An upload item keeps track of async operation state of each type of op.
 * These denote the different states that the async ops can have - to avoid polluting the original
 * AsyncOperationState with states that only the client handles & sets, we create an extension.
 */
export enum UploadSpecificAsyncState {
  DISABLED = '',
  QUEUED = 'queued',
  WAITING = 'waiting',
}

export type UploadAsyncState = UploadSpecificAsyncState | AsyncOperationState;

export const AsyncOperationStateKey: StateFieldName[] = [
  'transcodingState',
  'transcriptionState',
  'personDetectionState',
  'celebrityDetectionState',
  'labelDetectionState',
  'textDetectionState',
  'archiveFileTransferState'
];

export const AsyncOperationErrorMessageKey: ErrorMessageFieldName[] = [
  'transcodingErrorMessage',
  'transcriptionErrorMessage',
  'personDetectionErrorMessage',
  'celebrityDetectionErrorMessage',
  'labelDetectionErrorMessage',
  'textDetectionErrorMessage',
  'archiveFileTransferErrorMessage'
];

export enum UploadUIStatus {
  CANCELLED = 'Cancelling',
  QUEUED = 'Queued',
  PREPROCESSING = 'Preparing',
  UPLOADING = 'Uploading',
  TRANSCODING = 'Processing',
  WAITING = 'Waiting',
  ERROR = 'Error',
  PROCESSING = 'Analyzing',
  COMPLETED = 'Completed'
}

// Used to show a upload summary when a large upload batch has completed, because we want to
// reset the upload queue status so next time a user drops a batch it starts from scratch.
export interface UploadSummary {
  totalItems: number;
  totalSize: number;
}

export type ItemCreatedCallback = (val: { uploadItem: UploadItemBase, createdResponse?: ItemCreationResponse | LoadedItem }) => void;
export type UploadCompletedCallback = (val: { uploadItem: UploadItemBase }) => void;

export interface UploadContext {
  /**
   * If we detect we are in itemDetails page, we need to refresh the metadata
   * there.
   */
  refreshDisplayedMetadata(uploadItemId: string, metadata: ItemMetaDataDto): void;
  getCurrentFolderPath(): { name: string }[] | null;
  showCompletedUpload(itemId: string, progressInfo: ProgressInfo): Promise<void>
  getBackendClient(): BackendClient;
}

export abstract class UploadItemBase {

  // Determines how many items we will individually show&poll progress for. If more then this value are added,
  // the table will condense to a combined progress and no longer poll for transcoding/analytic status.
  public static MAX_CONCURRENT_VISIBLE_ITEMS = 20;

  public uploadProgress = 0;
  public uploadState = UploadState.WAITING;
  public localId: string;
  public useMultiPart = false;
  public uploader: MultiPartUploader | SimpleUploader | null = null;
  public title: string;
  public isHidden: boolean = false;

  // Used by ItemUploadDialog.vue to include/exclude a file from upload.
  public include: boolean = true;

  public transcodingState: UploadAsyncState = UploadSpecificAsyncState.WAITING;
  protected personDetectionState: UploadAsyncState = UploadSpecificAsyncState.DISABLED;
  protected transcriptionState: UploadAsyncState = UploadSpecificAsyncState.DISABLED;
  protected celebrityDetectionState: UploadAsyncState = UploadSpecificAsyncState.DISABLED;
  protected labelDetectionState: UploadAsyncState = UploadSpecificAsyncState.DISABLED;
  protected textDetectionState: UploadAsyncState = UploadSpecificAsyncState.DISABLED;
  protected archiveFileTransferState: UploadAsyncState = UploadSpecificAsyncState.DISABLED;
  protected transcodingErrorMessage: string | null = null;
  protected personDetectionErrorMessage: string | null = null;
  protected transcriptionErrorMessage: string | null = null;
  protected celebrityDetectionErrorMessage: string | null = null;
  protected labelDetectionErrorMessage: string | null = null;
  protected textDetectionErrorMessage: string | null = null;
  protected archiveFileTransferErrorMessage: string | null = null;
  public folderParents: string[] | null = null;

  public metadataErrors: MetadataErrors = {};
  public itemState: ItemStateType | null = null;
  public file: FileApi;
  public uploadToS3Clone: boolean;

  // Setting this to true will result in the item not being findable in search.
  public shouldBeHidden = false;

  public isP2Part = false;

  public routerLink: RouterLink | null = null;
  public parentItemIds: string[] | undefined;
  public relatedItemIds: string[] | undefined;

  // The item ID of the mogrt parent item that this upload item was extracted and added from.
  public mogrtParentItemId: string | undefined;
  public upstreamItemId: string | undefined;
  public sequenceDetails: SequenceDetails | undefined = undefined;

  public totalUploadedSize = 0;

  public tooltip: string | undefined;
  public derivedItemType: DynamoItemType;

  public id: string;
  // Whether this item is uploading media to an already existing item instead
  // of creating a new one.
  public readonly isExistingItem: boolean;
  public isCreated = false;
  public hasMetadataChanges = false;
  public mediaType: string;
  public targetUploadLocationId = DEFAULT_UPLOAD_LOCATION;
  public metadata: ItemMetaData | null = null;
  public visibleTo: ItemAccessControl | undefined = undefined;
  public loading = false;
  public isDeleted = false;
  public markedForDeletion = false;
  protected creatingItem = false;
  protected config: UploadConfig = defaultAnalyticConfig();
  public folderPath: string = '';
  public bucketConfig: CreateItemUploadLockResponseDto | undefined = undefined;
  public progressInfo: ProgressInfo | undefined;
  public createdBy: string | undefined = undefined;

  public missingDependenciesDownloadInfo: MissingDependenciesDownloadInfo | undefined = undefined;

  /**
   * Metadata snapshot for conflict detection.
   */
  public metadataSnapshot: MetadataSnapshot | null = null;

  /**
   * Metadata updater instance for handling metadata updates with conflict detection.
   */
  protected metadataUpdater: ItemMetadataUpdater | null = null;

  protected createdCallbacks: ItemCreatedCallback[] = [];
  protected completedCallbacks: UploadCompletedCallback[] = [];

  /**
   * Construct a view model for uploading media to an item.
   *
   * @param file The file that will be uploaded to this item
   * @param uploadToS3Clone If true, the folder path to upload the item to
   *                        will be simplified to prevent bugs like #1753
   * @param config The configuration for running analytics on this item
   * @param itemId Optional itemId - provide this if you want to perform an upload to an existing item.
   *               If omitted, an item will be created instead.
   */
  constructor(protected readonly context: UploadContext, file: FileApi, uploadToS3Clone: boolean, config: Partial<UploadConfig> = {}, itemId?: string) {
    this.file = file;
    this.uploadToS3Clone = uploadToS3Clone;

    this.localId = '' + ++lid;
    if (itemId) {
      this.isExistingItem = true;
      this.id = itemId;
    } else {
      this.isExistingItem = false;
      this.id = this.localId;
    }
    this.title = file?.name;
    // if file type exists and if it does not equal to octet-stream then
    // we are considering it. Otherwise we fall back to file type based on extension.

    // The browser detected MIME type in "file.type" is not always reliable for us, e.g.,
    // "text/plain" was being reported for a .ts file once, but we need it to be "video/mp2t".
    // So we are first checking our own mapping, and then falling back to the browser reported type.
    let mediaType = mediaTypeFromFilename(file.name);
    if (mediaType === 'application/octet-stream' && file.type && !isEmpty(file.type)) {
      mediaType = file.type;
    }
    this.mediaType = mediaType;
    this.derivedItemType = itemTypeFromMediaType(this.mediaType);
    this.sanitizeConfig(config);

    const currentPath = context.getCurrentFolderPath();
    if (currentPath && !this.uploadToS3Clone) {
      this.folderPath = constructUploadFolderPath(currentPath, '');
    }
  }

  private sanitizeConfig(config: Partial<UploadConfig>) {
    this.config.transcriptionEnabled = config.transcriptionEnabled ?? false;
    this.config.transcriptionConfig = config.transcriptionConfig;
    this.config.labelDetectionEnabled = config.labelDetectionEnabled ?? false;
    this.config.textDetectionEnabled = config.textDetectionEnabled ?? false;
    this.config.textDetectionSettings = config.textDetectionSettings;
    this.config.personDetectionEnabled = config.personDetectionEnabled ?? false;
    this.config.personDetectionConfig = config.personDetectionConfig;
    this.config.celebrityDetectionEnabled = config.celebrityDetectionEnabled ?? false;
    this.config.analyticPlugins = config.analyticPlugins;
    this.folderParents = config.folderId ? [config.folderId] : null;
    this.targetUploadLocationId = config.targetUploadLocationId ?? DEFAULT_UPLOAD_LOCATION;

    const listOfFolderNames = config.uploadFolderPath?.split('/').map(name => ({ name })) ?? [];
    this.folderPath = constructUploadFolderPath(listOfFolderNames, '') || this.folderPath;
  }

  public addCreatedItemCallback(callback: ItemCreatedCallback) {
    this.createdCallbacks.push(callback);
  }

  public getFilename() {
    /*
      TODO: The property this.file.name is only the basename of the file, which
      means that any "/" or "\\" inside of this string is intended to be part of
      the basename, and not be a path separator.

      It is therefore technically incorrect to call
      constructExportedSequenceFileName() here which transitively performs
      another getBasename() call with a given path separator, which may differ
      from the original context where the basename was captured.

      We should instead call a variant of constructExportedSequenceFileName()
      that does not do any additional interpretation of path separators.
    */
    const pathSep = '/';
    return this.sequenceDetails ? constructExportedSequenceFileName(this.file.name, pathSep, this.metadata) : this.file.name;
  }

  public removeCreatedItemCallback(callback: ItemCreatedCallback) {
    const index = this.createdCallbacks.indexOf(callback, 0);
    if (index > -1) {
      this.createdCallbacks.splice(index, 1);
    }
  }

  public addCompletedCallback(callback: ItemCreatedCallback) {
    this.completedCallbacks.push(callback);
  }

  public removeCompletedCallback(callback: UploadCompletedCallback) {
    const index = this.createdCallbacks.indexOf(callback, 0);
    if (index > -1) {
      this.completedCallbacks.splice(index, 1);
    }
  }

  public async onUploadCompleted(uploadMethods?: UploadMethods) {
    this.uploadState = UploadState.COMPLETE;
    this.uploadProgress = 100;
    invariant(this.file.size, 'Cannot upload item without proper file');
    if (this.file.size !== this.totalUploadedSize) {
      this.totalUploadedSize = this.file.size;
    }

    const uploadCompleteProgressInfo: ProgressInfo = {
      status: ProgressStep.CLIENT_UPLOAD_COMPLETE
    };
    this.progressInfo = uploadCompleteProgressInfo;
    await this.context.showCompletedUpload(this.id, uploadCompleteProgressInfo);

    let itemUpdateObject: ItemUpdateRequestDto = {
      progressInfo: uploadCompleteProgressInfo
    };

    if (this.bucketConfig) {
      const { storageConfigId, type, targetKey, isExternalBucket, bucket } = this.bucketConfig;
      if (needsIngestFileRequest(this.bucketConfig)) {
        if (storageConfigId && targetKey) {
          const dto: IngestFileDto = { configId: storageConfigId, srcKey: targetKey, placeholderId: this.id };
          void ingestUploadedFileFromExternalStorage(dto, this.context.getBackendClient())
            .catch((err) => console.error(`Error ingesting ${targetKey} with storage config: ${storageConfigId}:`, err));
        } else {
          console.warn('Unexpected: Missing storageConfigId or targetKey for ingest file request:', this.bucketConfig);
        }
      }

      if (isExternalBucket && type === 's3Bucket' && targetKey && bucket && storageConfigId) {
        itemUpdateObject = {
          ...itemUpdateObject,
          ...getSourceForExternalCloudIngestedItem(targetKey, bucket, storageConfigId),
          itemState: ItemState.HIGHRES_AVAILABLE,
          isExternalUpload: true,
          uploadMethods,
        };
        void api_updateItem(this.context.getBackendClient(), this.id, itemUpdateObject)
          .catch(err => console.error(`Error updating item: ${this.id} with ingest source after upload:`, err));
      }
    }

    for (const callback of this.completedCallbacks) {
      callback({
        uploadItem: this,
      });
    }

    this.completedCallbacks = [];
  }

  public async onUploadError() {
  }

  public updateFromItem(update: StandardItemEntryDto) {

    [...AsyncOperationStateKey].forEach(key => {
      if (update[key] !== undefined) {
        this[key] = update[key] as AsyncOperationState;
      }
    });

    [...AsyncOperationErrorMessageKey].forEach(key => {
      if (update[key]) {
        this[key] = update[key] as string;
      }
    });

    this.itemState = update.itemState;

    // Do not overwrite local changes, if any.
    if (!this.hasMetadataChanges) {
      this.metadata = update.metadata;
    }

    if (this.status === UploadUIStatus.ERROR) {
      this.tooltip = this.transcodingErrorMessage ? this.transcodingErrorMessage : 'Something went wrong during transcoding.';
    }
  }

  get status() {
    if (!this.isCreated) {
      return UploadUIStatus.QUEUED;
    } else if (this.isDeleted || this.uploadState === UploadState.CANCELLED) {
      return UploadUIStatus.CANCELLED;
    } else if (this.uploadState === UploadState.PREPROCESSING) {
      return UploadUIStatus.PREPROCESSING;
    } else if (this.isCreated && this.uploadState === UploadState.WAITING) {
      return UploadUIStatus.QUEUED;
    } else if (this.isUploading) {
      return UploadUIStatus.UPLOADING;
    } else if (this.transcodingState === AsyncOperationState.ERROR || this.uploadState === UploadState.ERROR) {
      return UploadUIStatus.ERROR;
    } else if (this.isTranscoding || this.isWaitingForTranscoding) {
      return UploadUIStatus.TRANSCODING;
    } else if (this.isProcessing) {
      return UploadUIStatus.PROCESSING;
    } else {
      return UploadUIStatus.COMPLETED;
    }
  }

  get mediaIsAvailable() {
    return this.status === UploadUIStatus.PROCESSING || this.status === UploadUIStatus.COMPLETED;
  }

  get isCreating() {
    return this.creatingItem;
  }

  get totalProgress() {
    const hasAnalytics = this.analyticsInQueue.length > 0;
    switch (this.status) {
      case UploadUIStatus.QUEUED: return 0;
      case UploadUIStatus.PREPROCESSING: return 0; // TODO: We can get some progress information from downloadMissingHighResDependencies in premiere to improve this part.
      case UploadUIStatus.UPLOADING: return 50 * this.uploadProgress / 100;
      case UploadUIStatus.TRANSCODING: return 50;
      case UploadUIStatus.PROCESSING: return !hasAnalytics ? 95 : 75 + (25 * this.completedAnalytics.length / this.analyticsInQueue.length);
      case UploadUIStatus.COMPLETED: return 100;
      case UploadUIStatus.ERROR: return 100;
      default: return 0;
    }
  }

  get isCancelled() {
    return this.uploadState === UploadState.CANCELLED;
  }

  get isUploading() {
    return this.uploadState !== UploadState.CANCELLED && this.uploadState !== UploadState.ERROR &&
           this.uploadState !== UploadState.COMPLETE;
  }

  get isTranscoding() {
    return this.transcodingState === AsyncOperationState.PROGRESSING;
  }

  get isWaitingForTranscoding() {
    return this.transcodingState === UploadSpecificAsyncState.WAITING;
  }

  get isProcessing() {
    return this.analyticsInQueue.length > 0 && this.completedAnalytics.length !== this.analyticsInQueue.length;
  }

  get isStarted() {
    return this.uploadState !== UploadState.WAITING && this.uploadState !== UploadState.PAUSED;
  }

  get analyticsInQueue() {
    const currentStatus: {label: string; state: UploadAsyncState, error: string | null}[] = [];
    [...AsyncOperationStateKey].forEach(key => {
      if (key !== 'transcodingState' && this[key] !== UploadSpecificAsyncState.DISABLED) {
        const errorState = getErrorStateFromOpField(key);
        currentStatus.push({
          label: userFriendlyLabelForOp(key),
          state: this[key],
          error: errorState ? this[errorState] : null
        });
      }
    });
    return currentStatus;
  }

  get failedAnalytics() {
    return this.analyticsInQueue.filter(op => op.state === AsyncOperationState.ERROR);
  }

  get ongoingAnalytics() {
    return this.analyticsInQueue.filter(op => op.state === AsyncOperationState.PROGRESSING);
  }

  get completedAnalytics() {
    return this.analyticsInQueue.filter(op => op.state === null || op.state === AsyncOperationState.COMPLETE || op.state === AsyncOperationState.ERROR);
  }

  get analyticStatusText() {
    if (this.analyticsInQueue.length === 0)
      return 'N/A';
    if (this.isUploading || this.isTranscoding || (this.ongoingAnalytics.length === 0 && this.completedAnalytics.length === 0))
      return `${this.analyticsInQueue.length} Queued`;
    if (this.completedAnalytics.length === this.analyticsInQueue.length)
      return 'Completed';
    return `${this.completedAnalytics.length}/${this.analyticsInQueue.length} completed${this.failedAnalytics.length > 0 ? '*' : ''}`;
  }

  get hasCompleted() {

    if (this.isProcessing)
      return false;

    // If we get here, no analytic operations are currently still incomplete, we fallback to
    // returning the itemstate.
    const itemState = this.itemState !== null && this.itemState === ItemState.COMPLETE;
    return itemState;
  }

  public setConfig(config: Partial<UploadConfig>) {
    this.sanitizeConfig(config);
  }

  public onMetadataChange(payload: ItemMetaData, newChanges: boolean) {
    this.metadata = payload;
    this.hasMetadataChanges = this.hasMetadataChanges ? true : newChanges;
  }

  public async saveMetadata() {
    // Should never happen, but need it for typesafety.
    if (!this.metadata)
      throw Error('No metadata to save.');

    // Skip saving items that have errors.
    if (!isEmpty(this.metadataErrors)) {
      return false;
    }

    this.loading = true;
    await api_updateItem(this.context.getBackendClient(), this.id, {
      metadata: this.metadata
    });
    this.loading = false;
    this.hasMetadataChanges = false;
    return true;
  }

  abstract beforeUpload(priority: TaskPriority): Promise<undefined | ItemCreationResponse>;

  public static prepareItemConfigProps(item: ItemUpdateRequestDto, config: Partial<UploadConfig>) {
    item.transcriptionEnabled = config.transcriptionEnabled;
    item.languageCode = config.transcriptionConfig?.transcriptionSourceLanguage;
    item.transcriptionService = config.transcriptionConfig?.transcriptionService;
    item.labelDetectionEnabled = config.labelDetectionEnabled;
    item.textDetectionEnabled = config.textDetectionEnabled;
    item.personDetectionEnabled = config.personDetectionEnabled;
    item.createUnknownPersons = config.personDetectionConfig?.createUnknownPersons;
    item.celebrityDetectionEnabled = config.celebrityDetectionEnabled;
    item.transcriptionChannels = config.transcriptionConfig?.transcriptionChannels;
    item.transcriptionChannelIdentification = config.transcriptionConfig?.transcriptionChannelIdentification;
    item.textDetectionSettings = config.textDetectionSettings;
    item.analyticPlugins = config.analyticPlugins;
  }

  get itemAnalyticsProperties() {
    const itemAnalyticsProperties: ItemUpdateRequestDto = {};
    UploadItem.prepareItemConfigProps(itemAnalyticsProperties, this.config);
    return itemAnalyticsProperties;
  }

  public get permanentId() {
    return this.isCreated ? this.id : null;
  }

  protected initializeMetadataUpdater(): ItemMetadataUpdater {
    if (!this.metadataUpdater) {
      this.metadataUpdater = new ItemMetadataUpdater({
        updateMetadata: async (itemId, payload, options) => {
          const result = await api_updateItem(
            this.context.getBackendClient(),
            itemId,
            payload,
            options
          );
          invariant(result.metadata, 'updateMetadata: Expected metadata in response');
          invariant(result.metadataVersion !== undefined, 'updateMetadata: Expected metadataVersion in response');
          return {
            metadata: result.metadata,
            metadataVersion: result.metadataVersion
          };
        },
        getMetadata: async (itemId) => {
          return api_getItemFields(
            this.context.getBackendClient(),
            itemId,
            ['metadata', 'metadataVersion'] as const
          );
        }
      }, new ConsoleLogger());
    }
    return this.metadataUpdater;
  }
}

export class UploadItem extends UploadItemBase {

  constructor(context: UploadContext, file: FileApi, uploadToS3Clone: boolean, config: Partial<UploadConfig> = {}, itemId?: string) {
    super(context, file, uploadToS3Clone, config, itemId);
  }

  public op?: UploadOperation;

  public async beforeUpload(priority: TaskPriority): Promise<undefined | ItemCreationResponse> {
    invariant(this.file != null, 'Cannot upload item without a file');

    // To avoid creating more than one item for this upload by accident.
    if (this.isCreated)
      return undefined;

    if (this.config.personDetectionEnabled)
      this.personDetectionState = UploadSpecificAsyncState.QUEUED;

    if (this.config.transcriptionEnabled)
      this.transcriptionState = UploadSpecificAsyncState.QUEUED;

    if (this.config.celebrityDetectionEnabled)
      this.celebrityDetectionState = UploadSpecificAsyncState.QUEUED;

    if (this.config.labelDetectionEnabled)
      this.labelDetectionState = UploadSpecificAsyncState.QUEUED;

    if (this.config.textDetectionEnabled)
      this.textDetectionState = UploadSpecificAsyncState.QUEUED;

    this.creatingItem = true;
    let item: ItemCreationResponse | ExtendedItemDto;

    this.progressInfo = {
      status: 'Uploading to item',
      percentage: 0
    };

    // Create item if its not a placeholder. Otherwise do a partial update
    // towards the existing item.
    const fileName = this.getFilename();
    const relationPromises: ReturnType<typeof api_updateItemRelations>[] = [];
    let itemCreationResponse: ItemCreationResponse | undefined = undefined;
    if (!this.isExistingItem) {
      item = await api_createItem(
        this.context.getBackendClient(),
        this.title,
        fileName,
        this.mediaType,
        this.config,
        this.folderParents,
        this.parentItemIds,
        this.sequenceDetails,
        this.metadata !== null ? this.metadata : undefined,
        this.shouldBeHidden,
        this.isP2Part,
        this.sequenceDetails ? ItemType.VIDEO : undefined,
        this.progressInfo,
        { priority },
        this.relatedItemIds,
        this.upstreamItemId,
        this.visibleTo,
      );
      itemCreationResponse = cloneDeep(item);
    } else {
      const itemUpdate: ItemUpdateRequestDto = {};
      itemUpdate.mediaType = this.mediaType;
      // Deducing the item type again just in case the mediaType was modified.
      itemUpdate.itemType = this.sequenceDetails ? ItemType.VIDEO : itemTypeFromMediaType(this.mediaType);
      itemUpdate.originalFileName = fileName;
      itemUpdate.upstreamItemId = this.upstreamItemId;
      UploadItem.prepareItemConfigProps(itemUpdate, this.config);

      // Handle metadata update with conflict detection if metadata is being set
      if (this.metadata !== null) {
        // Initialize snapshot if not already done (should be set by dialog watcher)
        if (this.metadataSnapshot === null) {
          // Fallback: fetch current server state and store as initial snapshot
          const currentState = await api_getItemFields(
            this.context.getBackendClient(),
            this.id,
            ['metadata', 'metadataVersion'] as const
          );
          this.metadataSnapshot = {
            latestSnapshot: cloneDeep(currentState.metadata),
            metadataVersion: currentState.metadataVersion,
            conflictedFields: []
          };
        }

        const metadataUpdater = this.initializeMetadataUpdater();
        const updateParams: UpdateMetadataParams = {
          editBase: {
            metadata: this.metadataSnapshot.latestSnapshot,
            metadataVersion: this.metadataSnapshot.metadataVersion
          },
          editedMetadata: this.metadata
        };

        const result = await metadataUpdater.tryUpdateMetadata(this.id, updateParams);

        if (result.status === 'schema_conflict') {
          // Form type changed on server
          throw createMetadataFormTypeConflictError({
            itemId: this.id,
            conflictMetadata: result.conflictMetadata
          });
        }

        if (result.status === 'fields_conflict') {
          // Field values conflicted
          throw createMetadataConflictError({
            itemId: this.id,
            conflicts: result.conflictingFields,
            conflictMetadata: result.conflictMetadata
          });
        }

        // Success - update snapshot
        this.metadataSnapshot = {
          latestSnapshot: result.latest.metadata,
          metadataVersion: result.latest.metadataVersion,
          conflictedFields: []
        };

        // Metadata was updated by ItemMetadataUpdater, now handle other fields
        if (this.sequenceDetails || this.visibleTo || this.progressInfo) {
          const additionalUpdate: ItemUpdateRequestDto = {};
          if (this.sequenceDetails) additionalUpdate.sequenceDetails = this.sequenceDetails;
          if (this.visibleTo) additionalUpdate.visibleTo = this.visibleTo;
          if (this.progressInfo) additionalUpdate.progressInfo = this.progressInfo;

          item = await api_updateItem(this.context.getBackendClient(), this.id, additionalUpdate, { priority }) as ExtendedItemDto;
        } else {
          item = result.latest as any;
        }
      } else {
        // No metadata to update, proceed with regular update
        if (this.sequenceDetails) {
          itemUpdate.sequenceDetails = this.sequenceDetails;
        }

        if (this.visibleTo) {
          itemUpdate.visibleTo = this.visibleTo;
        }

        if (itemUpdate.metadata) {
          // Temporary debug- this is client side logging so it should be OK to log the metadata to the console.
          // Will allow us to compare server state with client state if we see the need to do so.
          console.log(`Updating metadata for ${this.id}:`, JSON.stringify(itemUpdate.metadata, null, 2));
        }

        itemUpdate.progressInfo = this.progressInfo;

        item = await api_updateItem(this.context.getBackendClient(), this.id, itemUpdate, { priority }) as ExtendedItemDto;
      }

      if ((item as LoadedItem).isP2Part === true && item.folderParents && item.folderParents.length === 1) {
        // If we want to upload to an existing P2 part item, we need to
        // upload to the expected folder on S3/minio
        const folderSegments = await api_getItemAncestry(this.context.getBackendClient(), item.id, undefined, priority);
        const pathItems = folderSegments[0].map<PathItem>((segment) => ({name: segment.name, folderId: segment.id}));
        if (this.uploadToS3Clone) {
          this.folderPath = constructS3CloneUploadPath(pathItems);
        } else {
          // We don't want the first level 'Tenant Space' to be part of the path
          const path = pathItems.slice(1);
          this.folderPath = path.map(s => s.name).join('/');
        }

        // Such uploads are only occurring when manually rescuing
        // failed uploads, we need to clear the lock in case the previous
        // upload attempt still has the lock
        await api_deleteItemUploadLock(this.context.getBackendClient(), item.id, { priority });
      }

      if (this.parentItemIds && this.parentItemIds.length) {
        relationPromises.push(
          api_updateItemRelations(this.context.getBackendClient(), this.id, RelationType.Parents, this.parentItemIds, { priority })
        );
      }

      // If we detect we are in itemDetails page, we need to refresh the metadata there.
      this.context.refreshDisplayedMetadata(this.id, item.metadata);
    }

    this.isCreated = true;
    this.creatingItem = false;
    this.createdBy = item.createdBy;

    this.id = item.id;
    this.routerLink = getRouterLink(this.derivedItemType, { id: this.id }, { returnTo: 'activity' });
    this.uploadState = UploadState.WAITING;

    this.metadata = item.metadata;
    this.title = item.metadata.formData[SystemDefaultField.Title] as string;

    if (this.relatedItemIds && this.relatedItemIds.length) {
      relationPromises.push(
        api_updateItemRelations(this.context.getBackendClient(), this.id, RelationType.Related, this.relatedItemIds, { priority })
      );
    }

    // Create the relation with mogrt parent item
    if (this.mogrtParentItemId) {
      relationPromises.push(
        api_updateItemRelations(this.context.getBackendClient(), this.id, RelationType.MogrtParent, [this.mogrtParentItemId], { priority })
      );
    }

    if (relationPromises.length) await Promise.all(relationPromises);

    for (const callback of this.createdCallbacks) {
      callback({
        uploadItem: this,
        createdResponse: item
      });
    }

    this.createdCallbacks = [];
    return itemCreationResponse;
  }
}
export class UploadPersonImageItem extends UploadItemBase {

  private personId: string;

  constructor(gui: UploadContext, file: FileApi, personId: string, uploadToS3Clone: boolean) {
    super(gui, file, uploadToS3Clone);
    this.personId = personId;
  }

  public async beforeUpload() {
    invariant(this.file != null, 'Cannot upload item without a file');

    if (this.isCreated) {
      return undefined;
    }
    const item = await api_createPersonImage(this.context.getBackendClient(), this.personId, this.file.name);
    this.id = item.id;
    this.routerLink = getRouterLink(this.derivedItemType, { id: this.id }, { returnTo: 'activity' });
    this.uploadState = UploadState.WAITING;
    this.isCreated = true;

    for (const callback of this.createdCallbacks) {
      callback({
        uploadItem: this,
      });
    }
    this.createdCallbacks = [];
  }
}


const getErrorStateFromOpField = (field: StateFieldName): ErrorMessageFieldName | null => {
  switch(field) {
    case 'celebrityDetectionState': return 'celebrityDetectionErrorMessage';
    case 'personDetectionState': return 'personDetectionErrorMessage';
    case 'labelDetectionState': return 'labelDetectionErrorMessage';
    case 'textDetectionState': return 'textDetectionErrorMessage';
    case 'transcriptionState': return 'transcriptionErrorMessage';
    case 'transcodingState': return 'transcodingErrorMessage';
    default: return null;
  }
};

const userFriendlyLabelForOp = (field: StateFieldName): string => {
  switch(field) {
    case 'celebrityDetectionState': return 'Celebrity detection';
    case 'personDetectionState': return 'Face detection';
    case 'labelDetectionState': return 'Label detection';
    case 'textDetectionState': return 'Text detection';
    case 'transcriptionState': return 'Transcription';
    case 'transcodingState': return 'Processing';
    default: return '';
  }
};

export interface UploadOperation {
  progress: FileSetProgress;
  cancel: Canceler
}

export function needsIngestFileRequest(uploadInfo: CreateItemUploadLockResponseDto) {
  // If the upload is to a s3 clone that is not monitored by Kelda,
  // we need to manually trigger the ingest processing.
  const { type, keldaId, } = uploadInfo;
  return !keldaId && type === 's3Clone';
}

/**
 * Send a request to the server to ingest a file in an external upload location.
 * Used to trigger ingest of files in storages that can't notify our backend automatically.
 */
const ingestUploadedFileFromExternalStorage = async (request: IngestFileDto, client: BackendClient) => {
  const fiveMinute = 60 * 5 * 1000;

  const backoff = new ExponentialBackoff({
    timeLimit: fiveMinute,
    isRetryable: (err: unknown) => isRetryable(
      err,
      [] // Passing empty array to allow retrying 'POST' requests
    )
  });
  await backoff.resiliently(
    async () => { await api_ingestFile(client, request); },
    neverCancel,
    (ms, cause, failureCount) => {
      console.warn(
        'Network connectivity issue: ingestFileFromExternalStorage will retry later',
        JSON.stringify({ ...allExceptionDetails(cause), retryMs: Math.round(ms), failureCount, stack: undefined })
      );
    }
  );
};

export interface AnalyticsForDependencies {
  videoConfig: AnalyticConfig | undefined;
  audioConfig: Partial<AnalyticConfig> | undefined;
  imageConfig: Partial<AnalyticConfig> | undefined;
}
